import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Volume, VolumeStatus } from '@/service/gpu-instance/volume-type';
import {
  MoreHorizontal,
  Expand,
  Link,
  Unlink,
  Copy,
  Trash2,
  Camera,
  Eye,
} from 'lucide-react';

interface VolumeActionsDropdownProps {
  volume: Volume;
  onView?: () => void;
  onResize?: () => void;
  onAttach?: () => void;
  onDetach?: () => void;
  onClone?: () => void;
  onSnapshot?: () => void;
  onDelete?: () => void;
}

export const VolumeActionsDropdown = ({
  volume,
  onView,
  onResize,
  onAttach,
  onDetach,
  onClone,
  onSnapshot,
  onDelete,
}: VolumeActionsDropdownProps) => {
  const canResize = volume.status === VolumeStatus.Available || volume.status === VolumeStatus.InUse;
  const canAttach = volume.status === VolumeStatus.Available && !volume.isAttached;
  const canDetach = volume.isAttached && volume.status === VolumeStatus.InUse;
  const canClone = volume.status === VolumeStatus.Available || volume.status === VolumeStatus.InUse;
  const canSnapshot = volume.status === VolumeStatus.Available || volume.status === VolumeStatus.InUse;
  const canDelete =
    volume.status === VolumeStatus.Available ||
    volume.status === VolumeStatus.Error;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {onView && (
          <DropdownMenuItem onClick={onView}>
            <Eye className="mr-2 h-4 w-4" />
            View Details
          </DropdownMenuItem>
        )}

        {onSnapshot && (
          <DropdownMenuItem onClick={onSnapshot} disabled={!canSnapshot}>
            <Camera className="mr-2 h-4 w-4" />
            Create Snapshot
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        {onResize && (
          <DropdownMenuItem onClick={onResize} disabled={!canResize}>
            <Expand className="mr-2 h-4 w-4" />
            Resize Volume
          </DropdownMenuItem>
        )}

        {onAttach && (
          <DropdownMenuItem onClick={onAttach} disabled={!canAttach}>
            <Link className="mr-2 h-4 w-4" />
            Attach to Instance
          </DropdownMenuItem>
        )}

        {onDetach && (
          <DropdownMenuItem onClick={onDetach} disabled={!canDetach}>
            <Unlink className="mr-2 h-4 w-4" />
            Detach from Instance
          </DropdownMenuItem>
        )}

        {onClone && (
          <DropdownMenuItem onClick={onClone} disabled={!canClone}>
            <Copy className="mr-2 h-4 w-4" />
            Clone Volume
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        {onDelete && (
          <DropdownMenuItem
            onClick={onDelete}
            disabled={!canDelete}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete Volume
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default VolumeActionsDropdown;
